import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Lightbulb } from 'lucide-react'
import { objectsApi } from '../api'
import { masterRunbooksApi } from '@/features/master-runbooks/api'
import { AutomationOptionLabel, findMethod, type MethodSuggestion } from '../utils/runbookAutomation'

interface MethodSuggestionBannerProps {
    /** Object tier — recommendation from the object's declared/captured facts. */
    objectId?: string | null
    /** Master tier — recommendation from the AI-generator inputs on the runbook. */
    masterId?: number | null
}

/**
 * A single, per-runbook hint about which Method the tasks in this runbook are likely to
 * need. Fetched ONCE when the runbook is displayed, not per task.
 *
 * Renders NOTHING when the backend has no confident answer — which is the common case,
 * and deliberate: this is advice about what will actually execute, so a wrong suggestion
 * is worse than none. It is a hint only; nothing is auto-applied, and the engineer still
 * picks the Method per task.
 */
export function MethodSuggestionBanner({ objectId, masterId }: MethodSuggestionBannerProps) {
    const { t } = useTranslation()
    const enabled = !!objectId || (masterId != null && masterId > 0)

    const { data: suggestion } = useQuery<MethodSuggestion | null>({
        queryKey: ['method-suggestion', objectId ?? `master:${masterId}`],
        queryFn: async () => {
            const res = objectId
                ? await objectsApi.getMethodSuggestion(objectId)
                : await masterRunbooksApi.getMethodSuggestion(masterId as number)
            return res.data?.data?.suggestion ?? null
        },
        enabled,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        // A missing suggestion must never surface as an error on the runbook screen.
        retry: false,
    })

    if (!suggestion) return null

    const option = findMethod(suggestion.method)
    const confidenceLabel =
        suggestion.confidence === 'high' ? t('objects.runbook.confidenceHigh')
        : suggestion.confidence === 'medium' ? t('objects.runbook.confidenceMedium')
        : t('objects.runbook.confidenceLow')

    return (
        <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/20 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <Lightbulb className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
                <span className="text-blue-900 dark:text-blue-200">
                    {t('objects.runbook.suggestedMethod')}
                </span>
                <AutomationOptionLabel
                    option={option}
                    label={t(option.labelKey)}
                    className="font-medium text-blue-900 dark:text-blue-100"
                />
                <span className="text-xs text-blue-800/70 dark:text-blue-300/70">
                    ({confidenceLabel}) — {suggestion.reason}
                </span>
            </div>
            {suggestion.alternates.length > 0 && (
                <div className="mt-1 pl-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-blue-800/70 dark:text-blue-300/70">
                    <span>{t('objects.runbook.alsoConsider')}</span>
                    {suggestion.alternates.map((a) => (
                        <span key={a.method} className="inline-flex items-center gap-1">
                            <AutomationOptionLabel option={findMethod(a.method)} label={t(findMethod(a.method).labelKey)} />
                            <span>— {a.reason}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}
